import { createClient } from '@/lib/supabase/client';

export interface Partner {
  id: string;
  name: string;
  logoUrl: string;
  websiteUrl?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PartnerInput {
  name: string;
  logoUrl: string;
  websiteUrl?: string;
  displayOrder: number;
  isActive?: boolean;
}

class PartnerService {
  async getAll(): Promise<Partner[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('partners')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Supabase error fetching partners:', error);
      throw new Error(`Failed to fetch partners: ${error.message}`);
    }
    
    return (data || []).map(this.mapFromDb);
  }

  async getActive(): Promise<Partner[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('partners')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Supabase error fetching active partners:', error);
      throw new Error(`Failed to fetch active partners: ${error.message}`);
    }
    
    return (data || []).map(this.mapFromDb);
  }

  async getById(id: string): Promise<Partner | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('partners')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async create(input: PartnerInput): Promise<Partner | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('partners')
      .insert([{
        name: input.name,
        logo_url: input.logoUrl,
        website_url: input.websiteUrl,
        display_order: input.displayOrder,
        is_active: input.isActive ?? true,
        created_by: user.id,
      }])
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async update(id: string, input: Partial<PartnerInput>): Promise<Partner | null> {
    const supabase = createClient();
    const updateData: any = {};
    
    if (input.name !== undefined) updateData.name = input.name;
    if (input.logoUrl !== undefined) updateData.logo_url = input.logoUrl;
    if (input.websiteUrl !== undefined) updateData.website_url = input.websiteUrl;
    if (input.displayOrder !== undefined) updateData.display_order = input.displayOrder;
    if (input.isActive !== undefined) updateData.is_active = input.isActive;

    const { data, error } = await supabase
      .from('partners')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message);
    
    return data ? this.mapFromDb(data) : null;
  }

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('partners')
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
  }

  private mapFromDb(data: any): Partner {
    return {
      id: data.id,
      name: data.name,
      logoUrl: data.logo_url,
      websiteUrl: data.website_url,
      displayOrder: data.display_order,
      isActive: data.is_active,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const partnerService = new PartnerService();